import {
  useInvalidateLaunchHistory,
  useInvalidateStepup,
  useInvalidateStepupStatus,
} from './stepup';

import { useMutation } from '@tanstack/react-query';
import {
  postStepupTime,
  purchaseStepupOffer,
  validateStepupOffer,
} from '@wwe-portal/web/services/client/stepup';

import { StepupOffer, OfferTypeEnum } from '@wwe-portal/entities/stepup.type';
import {
  usePendingPayment,
  useXsollaPayment,
} from '@wwe-portal/web/hooks/payment/use-xsolla-payment';
import { STORAGE_ITEM_PURCHASING_STEPUP_OFFER } from '@wwe-portal/web/utils/constants/keys';
import { useGetWebcoins } from './currency';

import { AxiosResponse } from 'axios';
import { wait } from '../../../../../packages/ui/lib/timer';
import { useGetPaymentContext } from '@wwe-portal/web/hooks/offer/use-get-payment-context';
import { useToast } from '../../../../../packages/ui/components/toast';
import { showDialog } from '../../../../../packages/ui/components/dialog';
import { addDDError } from '@wwe-portal/web/libs/analytics/datadog';
import { useTranslations } from 'next-intl';
import { VERIFY_DURATION_MS } from '@wwe-portal/web/utils/constants/offer';
import { NotEnoughWebCoinDialog } from '@wwe-portal/web/components/club-store/modals/NotEnoughWebCoinDialog';

import { getPaymentStore } from '../stores/payment-store';
import { useProcessingDialog } from '@wwe-portal/web/hooks/payment/processing-dialog';
import { useInvalidateOffer } from '@wwe-portal/web/hooks/payment/use-invalidate-offer';

export class NotSufficientWebcoins extends Error {
  constructor() {
    super('not sufficient webcoins');
  }

  static isNotSufficientWebcoins(apiResponse: AxiosResponse) {
    return apiResponse.data?.data?.message === 'not.enough.web.coins';
  }
}

const usePurchaseStepUpByWebcoin = () => {
  const { amount } = useGetWebcoins();
  const purchasingByAlternativeCurrency = async ({
    stepupOffer,
    stepupId,
  }: {
    stepupOffer: StepupOffer;
    stepupId: string;
  }) => {
    if (amount == null) return;
    // Check wallets
    if (
      stepupOffer.offerData.AlternativeCostAmount != null &&
      amount < stepupOffer.offerData.AlternativeCostAmount
    ) {
      throw new NotSufficientWebcoins();
    }

    // Check wallets
    const payload = {
      stepupId,
      offerId: stepupOffer.offerId,
      orderId: stepupOffer.orderId,
    };
    const purchaseResult = await purchaseStepupOffer(payload);
    if (NotSufficientWebcoins.isNotSufficientWebcoins(purchaseResult)) {
      throw new NotSufficientWebcoins();
    }
    if (!purchaseResult.data.data.success) {
      throw new Error(purchaseResult.data.data.message);
    }
  };

  return { purchasingByAlternativeCurrency };
};

const useVerifyPayment = (
  stepsUpId: number,
  {
    onCloseSuccessDialog,
    onError,
    onSuccess,
  }: {
    onCloseSuccessDialog: () => void;
    onError: (error: unknown) => void;
    onSuccess: ({
      refetchStatusInterval,
      orderId,
    }: {
      refetchStatusInterval: number;
      orderId: number;
    }) => void;
  }
) => {
  const t = useTranslations();
  const invalidateStatus = useInvalidateStepupStatus(stepsUpId);
  const invalidateOffer = useInvalidateOffer();
  const invalidateStepup = useInvalidateStepup();

  const {
    showSuccess,
    showProcess,
    modal: processingModal,
  } = useProcessingDialog({
    progressData: {
      title: t('payment_title_load'),
      description: '',
      duration: VERIFY_DURATION_MS,
    },
    successData: {
      title: t('payment_title_successful'),
      description: t('payment_description_successful'),
    },
  });

  const verifyPayment = async ({
    refetchStatusInterval,
    orderId,
  }: {
    refetchStatusInterval: number;
    orderId: number;
  }) => {
    try {
      if (!orderId) return;
      let currentClaimItems: number | undefined;
      const maxAttemps = 5;
      let retryAttemps = maxAttemps;

      const tryInvalidateStatus = async () => {
        do {
          const response = await invalidateStatus();
          currentClaimItems = response?.data.data.claimedItems;
          if (currentClaimItems != null && currentClaimItems < orderId) {
            const weight = Math.max(1, maxAttemps - retryAttemps);
            await wait(1000 * weight * weight);
          }
          retryAttemps--;
          if (retryAttemps <= 0) {
            throw new Error('max attempts exceeded');
          }
        } while (currentClaimItems != null && currentClaimItems < orderId);
      };

      showProcess();
      await Promise.all([
        tryInvalidateStatus(),
        wait(refetchStatusInterval),
        wait(VERIFY_DURATION_MS),
      ]);
      await invalidateOffer();
      await invalidateStepup();
      showSuccess(undefined, onCloseSuccessDialog);
      onSuccess({
        refetchStatusInterval,
        orderId,
      });
    } catch (e) {
      processingModal.hide();
      onError(e);
    }
  };

  return { verifyPayment, processingModal };
};

interface UsePurchaseStepupOfferProps {
  refetchStatusInterval: number;
  stepsUpId: number;
  onVerifySuccess: (payload: { orderId: number }) => void;
  onCloseSuccessDialog: () => void;
}

export const usePurchaseStepup = ({
  refetchStatusInterval,
  stepsUpId,
  onVerifySuccess,
  onCloseSuccessDialog,
}: UsePurchaseStepupOfferProps) => {
  const invalidateStepupStatus = useInvalidateStepupStatus(stepsUpId);

  const handleError = (error: unknown) => {
    if (error instanceof NotSufficientWebcoins) {
      showDialog(NotEnoughWebCoinDialog);
      return;
    }

    toast({
      variant: 'error',
      content: 'Transaction Failure',
    });

    addDDError('Stepup payment error', error as never);
    invalidateStepupStatus();
    getPaymentStore().getState().clearPayments();
  };

  const { verifyPayment, processingModal } = useVerifyPayment(stepsUpId, {
    onCloseSuccessDialog,
    onError: handleError,
    onSuccess: onVerifySuccess,
  });

  const { startPayment } = useXsollaPayment(
    STORAGE_ITEM_PURCHASING_STEPUP_OFFER,
    {
      onPaymentSuccess: async (data) => {
        const orderId = data.context.metadata?.orderId;

        if (orderId != null && typeof orderId === 'number') {
          await verifyPayment({
            refetchStatusInterval,
            orderId,
          });
        }
      },
      progressDialogEnabled: false,
    }
  );

  const { toast } = useToast();

  const getPaymentContext = useGetPaymentContext();
  const { purchasingByAlternativeCurrency } = usePurchaseStepUpByWebcoin();

  const purchaseUniversalOffer = async ({
    stepupOffer,
    stepupId,
  }: {
    stepupOffer: StepupOffer;
    stepupId: string;
  }) => {
    // Start Purchasing
    if (stepupOffer.offerData.OfferType === OfferTypeEnum.AlternativeCurrency) {
      // Purchasing by AlternativeCurrency
      await purchasingByAlternativeCurrency({ stepupId, stepupOffer });
      await verifyPayment({
        refetchStatusInterval,
        orderId: stepupOffer.orderId,
      });
    } else {
      await startPayment({
        isSkipValidation: true,
        offerData: stepupOffer.offerData,
        context: {
          ...getPaymentContext(stepupOffer.offerData),
          metadata: {
            stepsUpId,
            orderId: stepupOffer.orderId,
          },
        },
      });
    }
    return true;
  };

  const { mutate: startPurchase, isPending: isValidating } = useMutation({
    mutationFn: async ({
      stepupOffer,
      stepupId,
    }: {
      stepupOffer: StepupOffer;
      stepupId: string;
    }) => {
      const payload = {
        stepupId,
        offerId: stepupOffer.offerId,
        orderId: stepupOffer.orderId,
      };
      const validationResult = await validateStepupOffer(payload);
      if (!validationResult.data.data.success) {
        throw new Error(validationResult.data.data.message);
      }
      return await purchaseUniversalOffer({ stepupOffer, stepupId });
    },
    onError: handleError,
  });

  const pendingPayment = usePendingPayment();

  return {
    startPurchase,
    processingModal,
    isPaying:
      isValidating ||
      pendingPayment?.source === STORAGE_ITEM_PURCHASING_STEPUP_OFFER,
  };
};

export const usePostStepupLaunchTime = () => {
  const invalidate = useInvalidateLaunchHistory();
  return useMutation({
    mutationFn: postStepupTime,
    onSuccess: async () => {
      await invalidate();
    },
  });
};
